import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useParams, useNavigate } from 'react-router-dom';
import { gql } from '@apollo/client';
import Breadcrumb from '../components/Breadcrumb';
import M3Card from '../components/M3Card';
import M3LinearProgress from '../components/M3Progress';
import SiteFormDialog from '../components/SiteFormDialog';
import { DELETE_SITE } from '../graphql/mutations';
import { SITES_QUERY } from '../graphql/queries';

const SITE_QUERY = gql`
  query Site($id: ID!) {
    site(id: $id) {
      id
      name
      lat
      lon
      locality
      countryCode
      siteCode
      gravesCount
      figuresCount
      createdAt
    }
  }
`;

export default function SiteShowPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(SITE_QUERY, { variables: { id } });
  const [editing, setEditing] = useState(false);

  const [deleteSite] = useMutation(DELETE_SITE, {
    refetchQueries: [{ query: SITES_QUERY }],
    async onCompleted() {
      navigate('/sites');
    }
  });

  if (error)
    return (
      <div className="page-content">
        <p className="alert alert-danger">Error: {error.message}</p>
      </div>
    );

  if (loading)
    return (
      <div className="page-content">
        <M3LinearProgress indeterminate />
      </div>
    );

  const site = data?.site;
  if (!site) return <div className="page-content">Site not found.</div>;

  return (
    <div className="page-content detail-page">
      <Breadcrumb items={[{ label: 'Sites', link: '/sites' }, { label: site.name }]} />

      <div className="detail-header">
        <h1>{site.name}</h1>
        <div className="detail-actions">
          <button className="btn btn-secondary" onClick={() => setEditing(true)}>
            <span className="material-symbols-rounded md-18">edit</span>
            Edit
          </button>
          <button
            className="btn btn-text text-error"
            onClick={() => {
              if (confirm('Delete this site?')) deleteSite({ variables: { input: { id } } });
            }}
          >
            <span className="material-symbols-rounded md-18">delete</span>
            Delete
          </button>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-6">
          <M3Card variant="outlined">
            <M3Card.Header title="Location" />
            <M3Card.Body>
              <div className="detail-row">
                <span className="detail-label">Coordinates</span>
                <span className="detail-value">
                  {site.lat != null ? site.lat.toFixed(4) : '—'},
                  {site.lon != null ? site.lon.toFixed(4) : '—'}
                </span>
              </div>
              {site.locality && (
                <div className="detail-row">
                  <span className="detail-label">Locality</span>
                  <span className="detail-value">{site.locality}</span>
                </div>
              )}
              {site.siteCode && (
                <div className="detail-row">
                  <span className="detail-label">Site Code</span>
                  <span className="detail-value">{site.siteCode}</span>
                </div>
              )}
            </M3Card.Body>
          </M3Card>
        </div>

        <div className="col-md-6">
          <M3Card variant="outlined">
            <M3Card.Header title="Statistics" />
            <M3Card.Body>
              <div className="detail-row">
                <span className="detail-label">Graves</span>
                <span className="detail-value">{site.gravesCount || 0}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Figures</span>
                <span className="detail-value">{site.figuresCount || 0}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Created</span>
                <span className="detail-value">
                  {new Date(site.createdAt).toLocaleDateString()}
                </span>
              </div>
            </M3Card.Body>
          </M3Card>
        </div>
      </div>

      <SiteFormDialog
        open={editing}
        site={{ id }}
        onClose={() => setEditing(false)}
        refetchQueries={[
          { query: SITES_QUERY },
          { query: SITE_QUERY, variables: { id } }
        ]}
      />
    </div>
  );
}
